import { TimelyPlanName } from './TimelyPlanName';
import { PricingRegion } from './PricingRegion';
import {
  getPricingMatrix,
  getSmsPricing,
  PriceCalculations,
} from './PricingMatrix';

export const getPlanByPricingRegion = ({ pricingRegion, planId }) => {
  const _plansInfo = {
    [TimelyPlanName.BASE]: {
      id: TimelyPlanName.BASE,
      name: 'Base',
      description:
        'For solo professionals who want to simplify appointments and payments. New customers only for a limited time. <a href="#FAQs" class="underline">T&Cs apply</a>*.',
      featured: false,
      cta: 'Try Base for free',
      features: {
        title: 'Key features:',
        list: [],
      },
    },
    [TimelyPlanName.BUILD]: {
      id: TimelyPlanName.BUILD,
      name: 'Build',
      description:
        'For hair and beauty professionals who want more control and less admin.',
      featured: false,
      cta: 'Try Build for free',
      features: {
        title: 'Key features:',
        list: [],
      },
    },
    [TimelyPlanName.ELEVATE]: {
      id: TimelyPlanName.ELEVATE,
      name: 'Elevate',
      description:
        'For hair and beauty professionals who want to put their brand first and reduce admin.',
      featured: false,
      cta: 'Try Elevate for free',
      features: {
        title: 'Everything in Build, plus:',
        list: [],
      },
    },
    [TimelyPlanName.INNOVATE]: {
      id: TimelyPlanName.INNOVATE,
      name: 'Innovate',
      description:
        'For hair and beauty professionals who want to transform and put the client experience first.',
      featured: true,
      cta: 'Try Innovate for free',
      features: {
        title: 'Everything in Elevate, plus:',
        list: [],
      },
    },
  };

  if (planId === TimelyPlanName.BASE) {
    _plansInfo[TimelyPlanName.BASE].features.list.push(
      'Full calendar management and online booking',
      'Send unlimited email reminders to customers',
    );
  }

  if (planId === TimelyPlanName.BUILD) {
    _plansInfo[TimelyPlanName.BUILD].features.list.push(
      'Social media booking and mini website',
      'Add your team and manage their day',
      `Send automated client reminders with SMS and email${pricingRegion === PricingRegion.ROW ? '<a href="#FAQs">*</a>' : ''}`,
    );

    _plansInfo[TimelyPlanName.BUILD].features.list.push('Manage stock levels');
  }

  if (planId === TimelyPlanName.BASE || planId === TimelyPlanName.BUILD) {
    if (
      [
        PricingRegion.NZL,
        PricingRegion.AUS,
        PricingRegion.GBR,
        PricingRegion.USA,
        PricingRegion.IRL,
      ].includes(pricingRegion)
    ) {
      _plansInfo[TimelyPlanName.BUILD].features.list.push(
        'Seamless in-person and online payments with simple fees',
      );
    }

    if ([PricingRegion.EUE].includes(pricingRegion)) {
      _plansInfo[TimelyPlanName.BUILD].features.list.push(
        'Online payments with simple fees',
      );
    }
  }

  if (planId === TimelyPlanName.ELEVATE) {
    _plansInfo[TimelyPlanName.ELEVATE].features.list.push(
      'Send digital consultation forms',
      'Store client photos and documents ',
      'Upsell effortlessly with add-on services',
      'Waitlist to manage appointment requests and cancellations',
    );

    if (
      [
        PricingRegion.NZL,
        PricingRegion.AUS,
        PricingRegion.GBR,
        PricingRegion.IRL,
        PricingRegion.USA,
        PricingRegion.CAN,
      ].includes(pricingRegion)
    ) {
      _plansInfo[TimelyPlanName.ELEVATE].features.list.push(
        'Targeted SMS campaigns and automated follow-up messages',
      );
    }

    _plansInfo[TimelyPlanName.ELEVATE].features.list.push(
      'Set pricing tiers based on staff skills and seniority',
    );
  }

  if (planId === TimelyPlanName.INNOVATE) {
    if (
      [
        PricingRegion.NZL,
        PricingRegion.AUS,
        PricingRegion.GBR,
        PricingRegion.IRL,
      ].includes(pricingRegion)
    ) {
      _plansInfo[TimelyPlanName.INNOVATE].features.list.push(
        'Direct phone support',
      );
    }

    _plansInfo[TimelyPlanName.INNOVATE].features.list.push(
      'Reduce admin with automatic consultation forms ',
      'Encourage rebooking with automated reminders',
      'Create and track staff sales targets and performance',
      'VIP services for special clients',
      'Send customers to a personalised booking confirmation page',
    );

    if (
      [PricingRegion.AUS, PricingRegion.USA, PricingRegion.CAN].includes(
        pricingRegion,
      )
    ) {
      _plansInfo[TimelyPlanName.INNOVATE].features.list.push(
        'Dedicated SMS number for client communication',
      );
    }
  }

  return _plansInfo[planId];
};

const makeStaffCountNumber = (staffCount) => {
  const staffCountNumber = parseInt(staffCount);
  return isNaN(staffCountNumber)
    ? 1
    : staffCountNumber === 0
      ? 1
      : staffCountNumber;
};

const planPrice = getPricingMatrix();
const smsPricing = getSmsPricing();

export const getPlanPrice = ({ pricingRegion, planId, staffCount }) => {
  staffCount = makeStaffCountNumber(staffCount);
  const planPriceSms = smsPricing[pricingRegion];
  const planPriceCountry = planPrice[pricingRegion];

  const calculatePlanPrices = PriceCalculations(planPriceCountry);
  const priceType =
    staffCount === 1 ? 'solo' : staffCount >= 8 ? 'fixed' : 'staff';

  const freeSmsPerStaffPerMonth =
    planPriceSms?.freeSmsPerStaffPerMonth?.[planId] * staffCount;

  let total = calculatePlanPrices({ plan: planId, staffCount });
  let price = 0;

  if (priceType === 'fixed') {
    total = calculatePlanPrices({ plan: planId, staffCount });
    price = (total / staffCount).toFixed(2);
  } else {
    total = calculatePlanPrices({ plan: planId, staffCount });
    price = planPriceCountry.prices?.[priceType]?.[planId];
  }

  /* eslint-disable operator-linebreak */
  const isDisabled =
    false ||
    (planId === TimelyPlanName.BASE && staffCount > 1) ||
    (planId === TimelyPlanName.BUILD && staffCount > 7) ||
    staffCount > 19;
  /* eslint-enable operator-linebreak */

  return {
    isDisabled,
    priceTag: planPriceCountry.tag,
    currencyCode: planPriceCountry.currencyCode,
    sign: planPriceCountry.sign,
    price,
    total,
    freeSmsPerStaffPerMonth,
  };
};
